import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Check, Save, Loader2, WifiOff, Zap, Scale, Crown, RotateCcw, AlertCircle,
  Sparkles, ClipboardCheck, FileText, MousePointerClick, Info, Puzzle, Bot, Wand2,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Button from "@/components/ui/Button";
import Modal from "@/components/dashboard/Modal";
import { cn } from "@/lib/cn";
import { useApiData } from "@/hooks/useApiData";
import LoadError from "@/components/dashboard/LoadError";
import { getModelConfig, updateModelConfig, getAiConfig } from "@/api/admin";
import { toastError } from "@/lib/toast";

// Claude tiers.
const H = "claude-haiku-4-5", S = "claude-sonnet-4-6", O = "claude-opus-4-8";
// OpenAI tiers.
const GM = "gpt-4o-mini", G41 = "gpt-4.1", G5 = "gpt-5";
// Ollama Cloud tiers (smoke-tested: gpt-oss:20b = fastest + accurate selectors).
// NOTE: kimi-k3 is deliberately NOT offered — it is "extra usage only" on the standard
// plan and answers 402, which aborts the run. Every tier here is plan-covered.
const OL_FAST = "gpt-oss:20b", OL_MID = "glm-5.2", OL_MAX = "kimi-k2.7-code";

const COLOR = {
  emerald: { solid: "bg-emerald-600 text-white", soft: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300", dot: "bg-emerald-500" },
  sky: { solid: "bg-sky-600 text-white", soft: "bg-sky-500/10 text-sky-600 dark:text-sky-300", dot: "bg-sky-500" },
  brand: { solid: "bg-brand-600 text-white", soft: "bg-brand-500/10 text-brand-600 dark:text-brand-300", dot: "bg-brand-500" },
};

// One model set per active AI. `ids` is [fast, mid, max]; `canon` maps any stored
// id (incl. versioned/reasoning ones) to the nearest tier button.
const SETS = {
  anthropic: {
    label: "Claude", ids: [H, S, O],
    models: {
      [H]: { label: "Haiku", desc: "Cheapest · fastest", color: "emerald" },
      [S]: { label: "Sonnet", desc: "Strong + efficient", color: "sky" },
      [O]: { label: "Opus", desc: "Most capable", color: "brand" },
    },
    canon: (id) => { const s = String(id || "").toLowerCase(); if (s.includes("opus")) return O; if (s.includes("sonnet")) return S; return H; },
  },
  openai: {
    label: "ChatGPT", ids: [GM, G41, G5],
    models: {
      [GM]: { label: "GPT-4o mini", desc: "Cheap · weak (avoid for agents)", color: "emerald" },
      [G41]: { label: "GPT-4.1", desc: "Recommended · strong + fast", color: "sky" },
      [G5]: { label: "GPT-5", desc: "Strongest · reasoning (slow/pricey)", color: "brand" },
    },
    canon: (id) => {
      const s = String(id || "").toLowerCase();
      if (s.includes("gpt-5") || s.startsWith("o1") || s.startsWith("o3") || s.startsWith("o4")) return G5;
      if (s.includes("mini") || s.includes("nano")) return GM;
      return G41;
    },
  },
  ollama_cloud: {
    label: "Ollama", ids: [OL_FAST, OL_MID, OL_MAX],
    models: {
      [OL_FAST]: { label: "GPT-OSS 20B", desc: "Recommended · fast, accurate selectors", color: "emerald" },
      [OL_MID]: { label: "GLM 5.2", desc: "Strong all-round", color: "sky" },
      [OL_MAX]: { label: "Kimi K2.7 Code", desc: "Strongest · agentic (slower)", color: "brand" },
    },
    canon: (id) => {
      const s = String(id || "").toLowerCase();
      if (s.includes("kimi") || s.includes("deepseek-v4-pro") || s.includes("ultra")) return OL_MAX;
      if (s.includes("glm") || s.includes("minimax") || s.includes("deepseek")) return OL_MID;
      return OL_FAST;  // gpt-oss, gemma, nemotron-nano, etc.
    },
  },
};

// Per-agent config field, per provider.
const AGENTS = [
  { role: "planner", label: "Planner Agent", hint: "Builds the test plan", icon: Sparkles, k: { anthropic: "planner_model", openai: "openai_planner_model", ollama_cloud: "ollama_planner_model" } },
  { role: "validator", label: "Validator Agent", hint: "The “judge” — verifies outcomes", icon: ClipboardCheck, k: { anthropic: "validator_model", openai: "openai_validator_model", ollama_cloud: "ollama_validator_model" } },
  { role: "reporter", label: "Reporter Agent", hint: "Writes the bug report", icon: FileText, k: { anthropic: "reporter_model", openai: "openai_reporter_model", ollama_cloud: "ollama_reporter_model" } },
  { role: "selector", label: "Selector Agent", hint: "Finds elements on the page", icon: MousePointerClick, k: { anthropic: "selector_model", openai: "openai_selector_model", ollama_cloud: "ollama_selector_model" } },
];
const EXT_KEY = { anthropic: "extension_planner_model", openai: "openai_extension_model", ollama_cloud: "ollama_extension_model" };
const EXE_KEY = { anthropic: "exe_planner_model", openai: "openai_exe_model", ollama_cloud: "ollama_exe_model" };
// Test-case GENERATION model — its own strong model, independent of the loop.
const TESTGEN_KEY = { anthropic: "testgen_model", openai: "openai_testgen_model", ollama_cloud: "ollama_testgen_model" };

const PRESET_META = {
  Cheapest: { icon: Zap, desc: "Fastest tier across all agents", color: "emerald" },
  Balanced: { icon: Scale, desc: "Stronger judge · fast elsewhere", color: "sky", tag: "Recommended" },
  Max: { icon: Crown, desc: "Top judge · mid elsewhere", color: "brand" },
};
// tier indices per preset → [planner, validator, reporter, selector]
const PRESET_TIERS = {
  Cheapest: [0, 0, 0, 0],
  Balanced: [0, 1, 0, 0],
  Max: [1, 2, 1, 1],
};

const DEFAULTS = {
  provider: "anthropic",
  planner_model: H, validator_model: S, reporter_model: H, selector_model: H,
  extension_planner_model: H, exe_planner_model: H,
  openai_planner_model: G41, openai_validator_model: G41, openai_reporter_model: G41,
  // The .exe agent must NOT default to a reasoning model (gpt-5 / o-series): it calls the
  // model once per browser STEP, and a reasoning model thinks for 30-120s before its first
  // byte — longer than a step, so every case times out before it can even log in. Reasoning
  // belongs on one-shot jobs (generation, the judge), never on the per-step loop.
  openai_selector_model: G41, openai_extension_model: G41, openai_exe_model: G41,
  ollama_planner_model: OL_FAST, ollama_validator_model: OL_FAST, ollama_reporter_model: OL_FAST,
  ollama_selector_model: OL_FAST, ollama_extension_model: OL_FAST, ollama_exe_model: OL_FAST,
  // Generation model per provider — default to a STRONG model (Ollama → GLM 5.2).
  testgen_model: S, openai_testgen_model: G41, ollama_testgen_model: OL_MID,
  // 0 = no ceiling. It shipped as 1500 — the same size as an ordinary request —
  // so it clamped every larger call and truncated test generation mid-JSON.
  temperature: 0, max_tokens: 0,
};

// Upper bound of the temperature range, per provider.
const TEMP_MAX = { anthropic: 1, openai: 2, ollama_cloud: 2 };

function matchPreset(cfg, set, provider) {
  for (const [name, tiers] of Object.entries(PRESET_TIERS)) {
    if (AGENTS.every((a, i) => set.canon(cfg[a.k[provider]]) === set.ids[tiers[i]])) return name;
  }
  return "Custom";
}

export default function ModelConfig() {
  const { data, loading, demo, error, reload, setData } = useApiData(getModelConfig, DEFAULTS);
  const { data: aicfg, loading: aiLoading, error: aiError, reload: reloadAi } = useApiData(getAiConfig, null);
  const [cfg, setCfg] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  // Which provider's models are being edited while the Active AI is AUTO. Declared up
  // here with the other hooks because the component returns early below, and a hook
  // after a conditional return breaks the rules-of-hooks ordering.
  const [tab, setTab] = useState("");

  const loaded = useMemo(() => ({ ...DEFAULTS, ...(data || {}) }), [data]);
  useEffect(() => { setCfg(loaded); }, [loaded]);

  // The active AI (from Settings → AI) decides which model set this page edits.
  // Null when the master switch has never been set ("" from /admin/ai-config):
  // the roles are then routed per-role from the environment, so which fields the
  // running agents read is unknown and guessing Claude would edit the wrong set.
  //
  // AUTO is the exception: it is not one provider, it is a POOL, and every key in that
  // pool answers with its OWN model. So there is no single set to edit — the page offers
  // one tab per pooled provider instead. Treating auto as "unset" (which is what a plain
  // SETS lookup does) locked this page out of exactly the mode that needs it most.
  const isAuto = !!aicfg && aicfg.active_provider === "auto";
  const pool = ((aicfg && aicfg.auto_pool) || []).filter((p) => SETS[p]);
  const activeProvider = isAuto
    ? (SETS[tab] && pool.includes(tab) ? tab : pool[0] || null)
    : (aicfg && SETS[aicfg.active_provider] ? aicfg.active_provider : null);
  const provider = activeProvider || "anthropic";
  const set = SETS[provider];
  // Providers disagree about the top of the temperature range: Anthropic caps at
  // 1, OpenAI accepts up to 2. One constant drives both the slider and the
  // validator, so the UI can never offer a value the check refuses (or hide one
  // it would accept).
  const tempMax = TEMP_MAX[provider] ?? 1;

  const preset = matchPreset(cfg, set, provider);
  const dirty = JSON.stringify(cfg) !== JSON.stringify(loaded);
  const weightOf = (id) => [1, 3, 5][set.ids.indexOf(set.canon(id))] ?? 1;
  // A unitless tier index, NOT money: there is no per-model price table behind
  // these weights, and they are identical for Claude, OpenAI and Ollama — so
  // rendering them with a dollar sign invented a figure nobody could budget on.
  const costIndex = AGENTS.reduce((s, a) => s + weightOf(cfg[a.k[provider]]), 0);
  const maxCostIndex = AGENTS.length * 5;

  const setField = (key, value) => { setCfg((c) => ({ ...c, [key]: value })); setSaved(false); };
  const applyPreset = (name) => {
    const tiers = PRESET_TIERS[name];
    setCfg((c) => { const n = { ...c }; AGENTS.forEach((a, i) => { n[a.k[provider]] = set.ids[tiers[i]]; }); return n; });
    setSaved(false);
  };

  // Changing the MODEL or the provider affects every test run, every generation
  // and every assistant reply from the next request onward — confirm that.
  // Tuning temperature or a token cap does not get in the way.
  const modelChanges = useMemo(() => {
    if (!aicfg) return [];
    const out = [];
    AGENTS.forEach((a) => {
      const key = a.k[provider];
      const before = (aicfg[key] ?? "");
      const after = (cfg[key] ?? "");
      if (String(before) !== String(after)) {
        out.push({ label: a.label || key, from: before || "—", to: after || "—" });
      }
    });
    return out;
  }, [aicfg, cfg, provider]);

  function requestSave() {
    if (saving) return;
    if (modelChanges.length > 0) { setConfirmOpen(true); return; }
    save();
  }

  async function save() {
    if (saving) return;
    // Bounds the API does not enforce for us. Without these an emptied field
    // became NaN -> 0 and was persisted as the platform-wide setting.
    // The SAME bound the slider uses, derived from the provider — the control
    // and the check cannot disagree, which is what let the slider cap at 1 while
    // the message quoted a range of 0–2.
    const temp = Number(cfg.temperature);
    if (!Number.isFinite(temp) || temp < 0 || temp > tempMax) {
      return toastError(`Temperature must be between 0 and ${tempMax}.`);
    }
    const maxTokens = Number(cfg.max_tokens);
    if (!Number.isInteger(maxTokens) || maxTokens < 0 || maxTokens > 200000
        || (maxTokens > 0 && maxTokens < 256)) {
      return toastError("Max tokens must be 0 (no ceiling) or between 256 and 200000.");
    }
    setSaving(true);
    try {
      const updated = await updateModelConfig(cfg);
      // The page used to keep showing the pre-save values as "loaded", so it
      // reported itself dirty forever and misrepresented what the server held.
      if (updated && typeof updated === "object") setData({ ...DEFAULTS, ...updated });
      else await reload();
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) { toastError(e.message || "Couldn't save the model configuration."); } finally { setSaving(false); setConfirmOpen(false); }
  }

  const demoBanner = demo ? (
    <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
      <WifiOff size={15} /> Backend not reachable — changes won&apos;t persist.
    </div>
  ) : null;

  if (loading || aiLoading) return <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>;

  // The editor must not render without both reads. `loaded` falls back to
  // DEFAULTS when `data` is null, so a failed load would present frontend
  // constants as the saved platform config — and one click on a preset makes the
  // form dirty, after which Save PUTs those constants over the real config.
  if (error || aiError || !activeProvider) {
    return (
      <>
        <PageHeader title="Model Config" subtitle="Pick the model per agent — trade cost for accuracy." />
        {demoBanner}
        <LoadError message={error} onRetry={reload} />
        <LoadError message={aiError} onRetry={reloadAi} />
        {!demo && !error && !aiError && (
          <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertCircle size={15} />
            {isAuto
              ? "Auto is on but no provider key is saved yet — add one in Settings → AI / API Key, then come back to choose its models."
              : "Active AI isn’t set — pick one in Settings → AI / API Key, then come back to choose its models."}
          </div>
        )}
      </>
    );
  }

  // A shared tier picker bound to a config field.
  const Picker = ({ field, className }) => {
    const cur = set.canon(cfg[field]);
    return (
      <div className={cn("flex gap-1.5 rounded-xl bg-slate-100 p-1 dark:bg-white/5", className)}>
        {set.ids.map((mid) => {
          const m = set.models[mid];
          const active = cur === mid;
          const c = COLOR[m.color];
          return (
            <button key={mid} type="button" onClick={() => setField(field, mid)} title={m.desc}
              className={cn("relative flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors",
                active ? cn(c.solid, "shadow-soft") : "text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white")}>
              {m.label}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <PageHeader
        title="Model Config"
        subtitle={isAuto
          ? `Auto is on — pick the model per agent for each key. Showing ${set.label}.`
          : `Pick the model per agent for ${set.label} — trade cost for accuracy.`}
        actions={
          <div className="flex items-center gap-2">
            {dirty && <Button variant="ghost" size="sm" onClick={() => setCfg(loaded)}><RotateCcw size={15} /> Discard</Button>}
            <Button onClick={requestSave} size="sm" disabled={saving || !dirty}>
              {saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : saved ? <><Check size={15} /> Saved</> : <><Save size={15} /> Save changes</>}
            </Button>
          </div>
        }
      />

      {demoBanner}

      {/* One tab per key in the Auto pool. Each key answers with its OWN model, so all of
          them need choosing — a single set of pickers would silently edit one and leave
          the others on whatever they defaulted to. */}
      {isAuto && pool.length > 0 && (
        <div className="mb-5">
          <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1 dark:border-white/10 dark:bg-white/5">
            {pool.map((p) => (
              <button key={p} type="button" onClick={() => setTab(p)}
                className={cn("rounded-lg px-4 py-2 text-sm font-semibold transition-colors",
                  provider === p
                    ? "bg-white text-brand-600 shadow-sm dark:bg-white/10 dark:text-brand-300"
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200")}>
                {SETS[p].label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-400">
            Auto shares work across these keys, and each one runs its own model — pick the models
            for each. Edits are kept while you switch tabs; one Save stores them all.
          </p>
        </div>
      )}

      {/* Active-AI banner: this page edits the models for whichever AI is active. */}
      <div className="mb-5 flex flex-col gap-1 rounded-xl border border-brand-200 bg-brand-50/50 px-4 py-3 text-sm dark:border-brand-500/20 dark:bg-brand-500/10 sm:flex-row sm:items-center sm:justify-between">
        <span className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <Info size={16} className="text-brand-500" />
          {isAuto
            ? <>Active AI: <b className="text-slate-900 dark:text-white">Auto</b> — the models below apply to <b className="text-slate-900 dark:text-white">{set.label}</b>.</>
            : <>Active AI: <b className="text-slate-900 dark:text-white">{set.label}</b> — the models below apply to it.</>}
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400">Change the active AI in Settings → AI / API Key.</span>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.5fr_1fr]">
        <div className="space-y-5">
          {/* presets */}
          <div className="card p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Quick preset</h3>
              {preset === "Custom" && <span className="rounded-full bg-brand-500/10 px-2.5 py-0.5 text-xs font-semibold text-brand-600 dark:text-brand-300">Custom</span>}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {Object.entries(PRESET_META).map(([name, meta]) => {
                const active = preset === name;
                const c = COLOR[meta.color];
                return (
                  <button key={name} type="button" onClick={() => applyPreset(name)}
                    className={cn("relative flex flex-col items-start gap-1.5 rounded-2xl border p-4 text-left transition-all",
                      active ? "border-brand-400 bg-brand-50/50 shadow-soft dark:border-brand-500/40 dark:bg-brand-500/10" : "border-slate-200 hover:-translate-y-0.5 hover:border-brand-300 dark:border-white/10")}>
                    {meta.tag && <span className="absolute right-2.5 top-2.5 rounded-full bg-brand-600 px-1.5 py-0.5 text-[0.5625rem] font-bold text-white">{meta.tag}</span>}
                    <span className={cn("grid h-9 w-9 place-items-center rounded-xl", c.soft)}><meta.icon size={18} /></span>
                    <span className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-slate-800 dark:text-white">{name}{active && <Check size={14} className="text-brand-500" />}</span>
                    <span className="text-xs text-slate-400">{meta.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* per-agent model picker */}
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Per-agent model</h3>
            <div className="mt-3 space-y-3">
              {AGENTS.map((agent, i) => (
                <motion.div key={agent.role} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="flex flex-col gap-3 rounded-2xl border border-slate-100 p-4 dark:border-white/5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300"><agent.icon size={18} /></span>
                    <div>
                      <p className="text-sm font-semibold text-slate-800 dark:text-white">{agent.label}</p>
                      <p className="text-xs text-slate-400">{agent.hint}</p>
                    </div>
                  </div>
                  <Picker field={agent.k[provider]} className="sm:flex-none" />
                </motion.div>
              ))}
            </div>

            {/* advanced */}
            <div className="mt-5 grid gap-4 border-t border-slate-100 pt-5 dark:border-white/5 sm:grid-cols-2">
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Temperature</span>
                  <span className="text-sm font-bold text-slate-900 dark:text-white">{Number(cfg.temperature).toFixed(1)}</span>
                </div>
                {/* The bound comes from the PROVIDER. The slider was fixed at
                    max=1 while the validator allowed up to 2, so half the
                    documented range was unreachable and the error message
                    described a value the control could not produce. */}
                <input type="range" min="0" max={tempMax} step="0.1" value={cfg.temperature}
                  onChange={(e) => setField("temperature", Number(e.target.value))}
                  aria-label={`Temperature (0 to ${tempMax})`}
                  className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-brand-600 dark:bg-white/10" />
                <p className="mt-1 text-xs text-slate-400">Lower = more deterministic (recommended: 0). Ignored by OpenAI reasoning models.</p>
              </div>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Max tokens</span>
                <input type="number" step="100" min="0" max="200000" value={cfg.max_tokens} onChange={(e) => setField("max_tokens", Number(e.target.value))} className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white" />
                <p className="mt-1 text-xs text-slate-400">A ceiling on every reply, not a spend limit — you are billed for what the model actually writes. 0 = no ceiling (recommended); a low value truncates long replies and test generation loses the whole page.</p>
              </label>
            </div>
          </div>

          {/* generation model — writes the test cases (heavy reasoning; NOT the loop) */}
          <div className="card p-5">
            <div className="flex items-center gap-2">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300"><Wand2 size={16} /></span>
              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Generation model</h3>
                <p className="text-xs text-slate-400">Writes the test cases — separate from the agents that run them.</p>
              </div>
            </div>
            <Picker field={TESTGEN_KEY[provider]} className="mt-4" />
            <p className="mt-2 text-[0.6875rem] text-slate-400">Generation is a heavy reasoning job — use a <strong>strong</strong> model here (a weak one writes far fewer cases). It does not affect execution speed.</p>
          </div>

          {/* extension model */}
          <div className="card p-5">
            <div className="flex items-center gap-2">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300"><Puzzle size={16} /></span>
              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Extension model</h3>
                <p className="text-xs text-slate-400">The headed browser extension&apos;s planner — separate from the agents above.</p>
              </div>
            </div>
            <Picker field={EXT_KEY[provider]} className="mt-4" />
            <p className="mt-2 text-[0.6875rem] text-slate-400">One model call per headed step — the cheapest tier is fine for the loop; bump it for tricky sites.</p>
          </div>

          {/* exe agent model */}
          <div className="card p-5">
            <div className="flex items-center gap-2">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300"><Bot size={16} /></span>
              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Exe agent model</h3>
                <p className="text-xs text-slate-400">The headed .exe desktop agent&apos;s planner — separate from the agents and the extension.</p>
              </div>
            </div>
            <Picker field={EXE_KEY[provider]} className="mt-4" />
            <p className="mt-2 text-[0.6875rem] text-slate-400">The .exe agent calls the model once per step. First AI run uses this model; replays are free. Pick a <strong>fast</strong> model — a reasoning model (GPT-5, o-series) thinks for 30-120s per call, which is longer than a browser step allows, so runs time out before they can log in.</p>
          </div>
        </div>

        {/* right: summary */}
        <div className="space-y-5">
          <div className="card overflow-hidden">
            <div className="border-b border-slate-100 bg-gradient-to-br from-brand-500/5 to-fuchsia-500/5 px-5 py-4 dark:border-white/10">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{set.label} · Engine mode</p>
              <p className="mt-0.5 font-display text-xl font-bold text-slate-900 dark:text-white">{preset}</p>
            </div>
            <div className="space-y-2.5 p-5">
              {AGENTS.map((a) => {
                const m = set.models[set.canon(cfg[a.k[provider]])];
                const c = COLOR[m.color];
                return (
                  <div key={a.role} className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"><a.icon size={14} className="text-slate-400" /> {a.label.replace(" Agent", "")}</span>
                    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold", c.soft)}>
                      <span className={cn("h-1.5 w-1.5 rounded-full", c.dot)} /> {m.label}
                    </span>
                  </div>
                );
              })}
              <div className="mt-3 border-t border-slate-100 pt-3 dark:border-white/5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500 dark:text-slate-400">Relative cost / run</span>
                  <span className="font-display text-lg font-bold text-slate-900 dark:text-white">{costIndex} of {maxCostIndex}</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
                  <motion.div className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-sky-500 to-brand-500" initial={false} animate={{ width: `${Math.round((costIndex / maxCostIndex) * 100)}%` }} transition={{ type: "spring", stiffness: 200, damping: 30 }} />
                </div>
                <p className="mt-1 text-[0.6875rem] text-slate-400">Relative to the max tier across all agents.</p>
              </div>
            </div>
          </div>

          <div className="card p-5">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white"><Info size={15} /> How this maps to the engine</h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-600 dark:text-slate-300">
              <li className="flex gap-2.5"><Check size={16} className="mt-0.5 shrink-0 text-brand-500" /> Saved to the backend and applied to <code className="rounded bg-slate-100 px-1 dark:bg-white/10">settings</code> — new runs use it.</li>
              <li className="flex gap-2.5"><Check size={16} className="mt-0.5 shrink-0 text-brand-500" /> These are the <b>{set.label}</b> models — used because {set.label} is the active AI. Switch AI in Settings to edit the other set.</li>
              <li className="flex gap-2.5"><Check size={16} className="mt-0.5 shrink-0 text-brand-500" /> The <b>Validator</b> is the “judge” — give it a stronger model for accuracy.</li>
              <li className="flex gap-2.5"><Check size={16} className="mt-0.5 shrink-0 text-brand-500" /> The <b>Planner/Selector</b> run most often — keep them cheaper.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Model changes take effect on the NEXT request across every surface —
          test runs, test-case generation and the assistant. Runs already in
          flight keep the model they started with. */}
      <Modal
        open={confirmOpen}
        onClose={() => (saving ? null : setConfirmOpen(false))}
        title="Change the models in use?"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmOpen(false)} disabled={saving}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : null} Apply
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Every new test run, test-case generation and assistant reply will use these from the
            next request. Runs already in flight keep the model they started with.
          </p>
          <ul className="space-y-1.5 rounded-xl border border-slate-200 p-3 text-sm dark:border-white/10">
            {modelChanges.map((c) => (
              <li key={c.label} className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium text-slate-700 dark:text-slate-200">{c.label}</span>
                <span className="text-slate-400 line-through">{c.from}</span>
                <span className="text-slate-400">→</span>
                <span className="font-semibold text-slate-900 dark:text-white">{c.to}</span>
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    </>
  );
}
